import { useEffect, useRef, useState } from 'react'
import { Select, Spin, Tag } from 'antd'
import type { CSSProperties } from 'react'
import { colors, groupColors } from '@/pages/static'
import { getStrategyVersions } from '@/api'
import type { ManagerDetail, StrategyVersion } from '@/types'

export interface ManagerStepValue {
    manager: ManagerDetail | null
    strategyVersion: StrategyVersion | null
    stockPoolId: number | null
    stockPoolName: string
    tradeType: 'paper' | 'live'
}

interface Props {
    value: ManagerStepValue
    onChange: (v: ManagerStepValue) => void
    managers: ManagerDetail[]
    loading: boolean
    /** 从基金经理详情跳转时预选 */
    presetManagerId?: number | null
}

const tradeTypeOptions = [
    { value: 'paper', label: '模拟盘（推荐 · 安全测试策略）' },
    { value: 'live',  label: '实盘（提交审核后开始运营）' },
]

function statusTag(status: string) {
    if (status === 'active') {
        return <Tag className="is-round is-success text-10" style={{ padding: '1px 8px', marginLeft: 8 }}>已激活</Tag>
    }
    if (status === 'archived') {
        return <Tag className="is-round text-10" style={{ padding: '1px 8px', marginLeft: 8 }}>已归档</Tag>
    }
    return <Tag className="is-round text-10" style={{ padding: '1px 8px', marginLeft: 8 }}>{status}</Tag>
}

function pickDefaultVersion(versions: StrategyVersion[]) {
    return versions.find(v => v.status === 'active') ?? versions[0] ?? null
}

export default function ManagerStep({ value, onChange, managers, loading, presetManagerId }: Props) {
    const [versionLoading, setVersionLoading] = useState(false)
    const [versions, setVersions] = useState<StrategyVersion[]>([])
    const presetAppliedRef = useRef(false)

    const handleSelectManager = async (manager: ManagerDetail) => {
        onChange({
            ...value,
            manager,
            strategyVersion: null,
            stockPoolId: manager.default_stock_pool_id ?? null,
            stockPoolName: manager.default_stock_pool_name ?? '',
        })
        setVersions([])
        setVersionLoading(true)
        try {
            const res = await getStrategyVersions(manager.id, ['active', 'archived'])
            const list = res.versions ?? []
            // 激活优先，其余按激活/归档时间倒序展示
            const sorted = [...list].sort((a, b) => {
                if (a.status === 'active' && b.status !== 'active') return -1
                if (b.status === 'active' && a.status !== 'active') return 1
                const at = Date.parse(String(a.activated_at || a.archived_at || a.created_at || 0))
                const bt = Date.parse(String(b.activated_at || b.archived_at || b.created_at || 0))
                return bt - at
            })
            setVersions(sorted)
            onChange({
                ...value,
                manager,
                strategyVersion: pickDefaultVersion(sorted),
                stockPoolId: manager.default_stock_pool_id ?? null,
                stockPoolName: manager.default_stock_pool_name ?? '',
            })
        } catch {
            setVersions([])
        } finally {
            setVersionLoading(false)
        }
    }

    useEffect(() => {
        if (presetAppliedRef.current || !presetManagerId || loading || !managers.length || value.manager) return
        const preset = managers.find(m => m.id === presetManagerId)
        if (!preset) return
        presetAppliedRef.current = true
        void handleSelectManager(preset)
    }, [presetManagerId, managers, loading, value.manager])

    const handleSelectVersion = (versionId: number) => {
        const selected = versions.find(v => v.id === versionId) ?? null
        onChange({ ...value, strategyVersion: selected })
    }

    return (
        <section className="mgr-step">
            <div className="mgr-step__section">
                <div className="mgr-step__section-title">运营模式</div>
                <Select
                    value={value.tradeType}
                    options={tradeTypeOptions}
                    onChange={v => onChange({ ...value, tradeType: v })}
                    style={{ width: '100%', maxWidth: 400 }}
                />
            </div>

            <div className="mgr-step__section">
                <div className="mgr-step__section-title">选择组合经理</div>
                {loading ? (
                    <div className="mgr-step__spin"><Spin /></div>
                ) : (
                    <div className="mgr-step__manager-list">
                        {managers.map((m, i) => (
                            <div
                                key={m.id}
                                className={`mgr-step__manager-card${value.manager?.id === m.id ? ' is-selected' : ''}`}
                                style={{ '--mgr-color': colors[i % colors.length], '--mgr-group': groupColors[i % groupColors.length] } as CSSProperties}
                                onClick={() => handleSelectManager(m)}
                            >
                                <div className="mgr-step__avatar font-weight-700" style={{ background: colors[i % colors.length] }}>
                                    {m.initials}
                                </div>
                                <div className="mgr-step__manager-main">
                                    <div className="mgr-step__manager-name font-weight-700">{m.nickname}</div>
                                    <div className="mgr-step__manager-meta">{m.style} · {m.risk_level}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {value.manager && (
                <div className="mgr-step__section">
                    <div className="mgr-step__section-title">策略版本</div>
                    {versionLoading ? (
                        <div className="mgr-step__spin"><Spin size="small" /></div>
                    ) : versions.length > 0 ? (
                        <>
                            <Select
                                value={value.strategyVersion?.id}
                                placeholder="请选择策略版本"
                                style={{ width: '100%', maxWidth: 520, marginBottom: 12 }}
                                onChange={handleSelectVersion}
                                options={versions.map(v => ({
                                    value: v.id,
                                    label: `${v.version_no} · ${v.name}${v.status === 'active' ? '（已激活）' : '（已归档）'}`,
                                }))}
                            />
                            {value.strategyVersion && (
                                <div className="mgr-step__version-card">
                                    <div className="mgr-step__version-header">
                                        <span className="mgr-step__version-no font-weight-700">
                                            {value.strategyVersion.version_no}
                                        </span>
                                        <span className="mgr-step__version-name">{value.strategyVersion.name}</span>
                                        {statusTag(value.strategyVersion.status)}
                                        <Tag className={`is-round text-10 ${value.strategyVersion.market === 'HK' ? 'is-purple' : 'is-success'}`} style={{ padding: '1px 8px' }}>
                                            {value.strategyVersion.market}
                                        </Tag>
                                    </div>
                                    <div className="mgr-step__version-snapshot">
                                        {value.strategyVersion.strategy_snapshot}
                                    </div>
                                    {value.strategyVersion.constraints?.length > 0 && (
                                        <div className="mgr-step__version-constraints">
                                            {value.strategyVersion.constraints.map(c => (
                                                <Tag key={c} className="border-tag text-11 is-round-tag" style={{ marginRight: 6 }}>{c}</Tag>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mgr-step__version-readonly-tip">
                                        策略版本仅供参考，不可在此修改。如需调整，请前往「投资策略回测」模块。
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="mgr-step__empty-tip">
                            该基金经理暂无已激活或已归档的策略版本，无法创建组合。<br />
                            请先在「智能组合经理」模块激活一个策略版本。
                        </div>
                    )}
                </div>
            )}
        </section>
    )
}
